import { useState, useEffect } from "react";
import { Button, OutlineButton } from "components/button";
import { Button as ButtonV2 } from "components/buttonV2";
import GroupedButton from "components/button/grouped";
import axios from "axios";
import { dollarFormatter } from "utils";
import { StripeContainer } from "./components/StripeContainer";
import { getStripeAccountId } from "./api/getStripeAccountId";
import { useTranslation } from 'react-i18next';
import ManualContainer from "./components/ManualContainer";
import DepositRequestContainer from "./components/DepositRequestContainer";

type Props = {
  id: string;
  type: "request" | "project" | "invoice" | "customer";
  is_deposit: boolean;
  amount_due?: number;
  customerId?: string;
};

export default function NewPaymentModal(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.payment.newPaymentModal";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { id, type, amount_due, customerId } = props;

  const [action, setAction] = useState("manual");
  const [stripeAccountId, setStripeAccountId] = useState<null | string>(null);
  const [stripeAccountError, setStripeAccountError] = useState<boolean>(false);
  const [stripeAccountLoading, setStripeAccountLoading] = useState<boolean>(true);
  const [divisions, setDivisions] = useState<{ id: number; label: string }[]>([]);

  const approved = type === "request" || type === "project" ? true : false;

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/divisions`)
      .then((res) => setDivisions(res.data))
      .catch((err) => console.error("Failed to load divisions", err));
  }, []);


  useEffect(() => {
    setStripeAccountLoading(true);
    console.log("NewPaymentModal: Fetching Stripe account ID...");
    getStripeAccountId()
      .then((data: string) => {
        console.log("NewPaymentModal: Stripe account ID loaded:", data);
        setStripeAccountId(data);
        setStripeAccountError(false);
        setStripeAccountLoading(false);
      })
      .catch((error) => {
        console.error("NewPaymentModal: Failed to load Stripe account ID:", error);
        console.error("NewPaymentModal: Error details:", error.response?.data || error.message);
        setStripeAccountError(true);
        setStripeAccountLoading(false);
        // Don't set stripeAccountId, which will prevent StripeContainer from rendering
        // This is intentional - we don't want to show Stripe payment option if account ID is missing
      });
  }, []);

  return (
    <div className="w-[600px] flex flex-col space-y-4">
      {amount_due && (
        <div className="text-green-600 border  border-green-600 bg-green-100/50 p-2 rounded">
          <span>{tr("Amount Due")}: {dollarFormatter(amount_due)} </span>
        </div>
      )}
      <div className="flex items-center space-x-2">
        <div>
          {approved ? (
            <GroupedButton>
              <OutlineButton
                size="small"
                onClick={() => setAction("auto")}
                $selected={action === "auto"}
              >
                Stripe
              </OutlineButton>
              <OutlineButton
                size="small"
                onClick={() => setAction("manual")}
                $selected={action === "manual"}
              >
                {tr("Manual")}
              </OutlineButton>
              <OutlineButton
                size="small"
                onClick={() => setAction("request")}
                $selected={action === "request"}
              >
                {tr("Deposit Request")}
              </OutlineButton>
            </GroupedButton>
          ) : (
            <GroupedButton>
              <OutlineButton
                size="small"
                onClick={() => setAction("auto")}
                $selected={action === "auto"}
              >
                Stripe
              </OutlineButton>
              <OutlineButton
                size="small"
                onClick={() => setAction("manual")}
                $selected={action === "manual"}
              >
                {tr("Manual")}
              </OutlineButton>
            </GroupedButton>
          )}
        </div>
      </div>
      {action === "auto" ? (
        stripeAccountLoading ? (
          <div className="p-4 text-center border border-gray-200 bg-gray-50 rounded">
            <p className="text-gray-600">{tr("Loading")} Stripe configuration...</p>
          </div>
        ) : stripeAccountError ? (
          <div className="p-4 border border-red-300 bg-red-50 rounded text-red-700">
            <p className="font-semibold">{tr("Stripe Payment Unavailable")}</p>
            <p className="text-sm mt-1">
              {tr("Unable to load Stripe payment configuration. Please use Manual payment or contact support.")}
            </p>
            <p className="text-xs mt-2 text-red-600">Check browser console for details.</p>
          </div>
        ) : stripeAccountId ? (
          (() => {
            console.log("NewPaymentModal: Rendering StripeContainer with stripeAccountId:", stripeAccountId);
            return <StripeContainer stripeAccountId={stripeAccountId} amountDue={amount_due} {...props} customerId={customerId} />;
          })()
        ) : (
          <div className="p-4 border border-yellow-300 bg-yellow-50 rounded text-yellow-700">
            <p className="font-semibold">Stripe configuration not loaded</p>
            <p className="text-sm mt-1">Please wait or try refreshing the page.</p>
          </div>
        )
      ) : action === "request" && approved ? (
        <DepositRequestContainer id={id} type={type} customerId={customerId} />
      ) : (
        <ManualContainer {...props} divisions={divisions} />
      )}
    </div>
  );
}

